// Library imports
import { Vector3 } from '@math.gl/core';
import { Vector2 } from '@math.gl/core';

// Local imports
import { Bbox, CrossSectionCoordsMeta } from '@typings/reality-capture/common';
import { CoordsUtils, NorthingEasting } from '@shared/utils/CoordsUtils';
import { MathUtils } from '@shared/utils/MathUtils';
import { RcPointsToCrossSectionPointsInput } from '@typings/reality-capture/common';
import { PointCloudPoint } from '@pages/cross-section/cross-section/CrossSectionStore';

export async function RcPointsToCrossSectionPoints(input: RcPointsToCrossSectionPointsInput): Promise<PointCloudPoint[]> {
    // Convert RealityCapturePoints to PointCloudPoints and correct for mercator distortion
    const crossSectionPointCloudPoints: PointCloudPoint[] = input.points.map(rcPoint => {
        const crossSectionPoint: Vector2 = CoordsUtils.worldCoordToCrossSectionCoord(rcPoint.point, input.startVector, input.endVector);
        const correctedPoint: Vector2 = new Vector2(crossSectionPoint.x / input.mercatorDistortion, crossSectionPoint.y);

        return {
            id: `${rcPoint.point.x}-${rcPoint.point.y}`,
            name: rcPoint.name,
            point: correctedPoint,
            sourceFileId: rcPoint.sourceFileId,
            sourceFileUuid: rcPoint.sourceFileUuid,
            color: input.color ? (input.color.split(',').map(Number) as [number, number, number]) : rcPoint.color,
            visible: true,
            csCanvas: input.csCanvas,
            onClickCallback: () => {
                /* For now don't do anything when clicking point cloud */
            },
        };
    });

    crossSectionPointCloudPoints.forEach(p => {
        input.pointCloudNamesMap[p.sourceFileUuid] = p.name;
    });

    return crossSectionPointCloudPoints;
}

export async function crossSectionCoordsMeta(startCoord: NorthingEasting, endCoord: NorthingEasting, rectangleWidth: number = 0.1): Promise<CrossSectionCoordsMeta> {
    const startLatLon = CoordsUtils.projectCoordinateToWgs84(startCoord);
    const endLatLon = CoordsUtils.projectCoordinateToWgs84(endCoord);

    const startMercator = CoordsUtils.wgs84ToWebMercator(startLatLon);
    const endMercator = CoordsUtils.wgs84ToWebMercator(endLatLon);
    const startVector = new Vector3(startMercator.e, startMercator.n, startMercator.z);
    const endVector = new Vector3(endMercator.e, endMercator.n, endMercator.z);

    const area: number[][] = MathUtils.getRectangleAroundLine2D([startMercator.e, startMercator.n], [endMercator.e, endMercator.n], rectangleWidth);

    const centerLatitude = (startLatLon.lat + endLatLon.lat) / 2;
    const mercatorDistortion: number = CoordsUtils.calculateMercatorDistortionFromLatitude(centerLatitude);

    return { area, startVector, endVector, mercatorDistortion };
}

export function areaToBbox(area: number[][]): Bbox {
    const minX = Math.min(...area.map(p => p[0]));
    const maxX = Math.max(...area.map(p => p[0]));
    const minY = Math.min(...area.map(p => p[1]));
    const maxY = Math.max(...area.map(p => p[1]));
    const bbox: Bbox = [minX, minY, maxX, maxY];
    return bbox;
}

export function getRandomPastelColor(): string {
    const r = Math.floor(Math.random() * 127 + 127);
    const g = Math.floor(Math.random() * 127 + 127);
    const b = Math.floor(Math.random() * 127 + 127);
    return `${r},${g},${b}`;
}
